import React, { useState } from "react";
import api from "../api";
import useModalEscape from "@/hooks/useModalEscape";
import "./ChatDebugModal.css";

/**
 * ChatDebugModal - Modal to display complete debug trace for a chat interaction
 *
 * Shows:
 * - System prompt and user question
 * - All tool calls and results
 * - Token usage and cost breakdown
 * - Processing time and error information
 */
const ChatDebugModal = ({ chatId, onClose }) => {
  useModalEscape(onClose);

  const [debugTrace, setDebugTrace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    systemPrompt: false,
    conversationFlow: false,
    toolResults: {},
  });

  React.useEffect(() => {
    const fetchDebugTrace = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("auth_token");
        const response = await fetch(
          `${api.defaults.baseURL}/chat/history/${chatId}/debug`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (!response.ok) {
          throw new Error(
            `Failed to fetch debug trace: ${response.statusText}`,
          );
        }

        const data = await response.json();
        setDebugTrace(data.debug_trace);
      } catch (err) {
        console.error("Error fetching debug trace:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDebugTrace();
  }, [chatId]);

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const toggleToolResult = (toolKey) => {
    setExpandedSections((prev) => ({
      ...prev,
      toolResults: {
        ...prev.toolResults,
        [toolKey]: !prev.toolResults[toolKey],
      },
    }));
  };

  const formatJson = (obj) => {
    return JSON.stringify(obj, null, 2);
  };

  if (loading) {
    return (
      <div className="debug-modal-overlay" onClick={onClose}>
        <div className="debug-modal" onClick={(e) => e.stopPropagation()}>
          <div className="debug-modal-header">
            <h2>🔍 Debug Trace</h2>
            <button className="debug-modal-close" onClick={onClose}>
              ×
            </button>
          </div>
          <div className="debug-modal-body">
            <div className="loading-spinner">Loading debug trace...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="debug-modal-overlay" onClick={onClose}>
        <div className="debug-modal" onClick={(e) => e.stopPropagation()}>
          <div className="debug-modal-header">
            <h2>🔍 Debug Trace</h2>
            <button className="debug-modal-close" onClick={onClose}>
              ×
            </button>
          </div>
          <div className="debug-modal-body">
            <div className="error-message">❌ {error}</div>
          </div>
        </div>
      </div>
    );
  }

  if (!debugTrace) {
    return null;
  }

  return (
    <div className="debug-modal-overlay" onClick={onClose}>
      <div className="debug-modal" onClick={(e) => e.stopPropagation()}>
        <div className="debug-modal-header">
          <h2>🔍 Debug Trace</h2>
          <button className="debug-modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="debug-modal-body">
          {/* Overview Section */}
          <div className="debug-section">
            <h3>📊 Overview</h3>
            <div className="debug-grid">
              <div className="debug-item">
                <span className="debug-label">Model:</span>
                <span className="debug-value">{debugTrace.model}</span>
              </div>
              <div className="debug-item">
                <span className="debug-label">Iterations:</span>
                <span className="debug-value">{debugTrace.iterations}</span>
              </div>
              <div className="debug-item">
                <span className="debug-label">Processing Time:</span>
                <span className="debug-value">
                  {debugTrace.processing_time_ms}ms
                </span>
              </div>
              <div className="debug-item">
                <span className="debug-label">Cost:</span>
                <span className="debug-value">
                  ${debugTrace.cost_usd?.toFixed(4)}
                </span>
              </div>
            </div>
          </div>

          {/* Token Usage Section */}
          <div className="debug-section">
            <h3>🎫 Token Usage</h3>
            <div className="debug-grid">
              <div className="debug-item">
                <span className="debug-label">Total:</span>
                <span className="debug-value">
                  {debugTrace.tokens?.total?.toLocaleString()}
                </span>
              </div>
              <div className="debug-item">
                <span className="debug-label">Prompt:</span>
                <span className="debug-value">
                  {debugTrace.tokens?.prompt?.toLocaleString()}
                </span>
              </div>
              <div className="debug-item">
                <span className="debug-label">Completion:</span>
                <span className="debug-value">
                  {debugTrace.tokens?.completion?.toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          {/* Question Section */}
          <div className="debug-section">
            <h3>❓ User Question</h3>
            <div className="debug-code">{debugTrace.question}</div>
          </div>

          {/* System Prompt Section */}
          <div className="debug-section">
            <h3>
              <button
                className="debug-toggle"
                onClick={() => toggleSection("systemPrompt")}
              >
                {expandedSections.systemPrompt ? "▼" : "▶"}
              </button>
              🎯 System Prompt ({debugTrace.system_prompt?.length || 0} chars)
            </h3>
            {expandedSections.systemPrompt && (
              <div className="debug-code">{debugTrace.system_prompt}</div>
            )}
          </div>

          {/* Conversation Flow Section */}
          {debugTrace.conversation_flow &&
            debugTrace.conversation_flow.length > 0 && (
              <div className="debug-section">
                <h3>
                  <button
                    className="debug-toggle"
                    onClick={() => toggleSection("conversationFlow")}
                  >
                    {expandedSections.conversationFlow ? "▼" : "▶"}
                  </button>
                  💬 Conversation Flow ({debugTrace.conversation_flow.length}{" "}
                  messages)
                </h3>
                {expandedSections.conversationFlow && (
                  <div className="debug-conversation-flow">
                    {debugTrace.conversation_flow.map((message, idx) => (
                      <div key={idx} className="debug-message">
                        <div className="debug-message-header">
                          <strong>
                            {message.role === "user"
                              ? "👤 User"
                              : "🤖 Assistant"}
                            :
                          </strong>
                          <span className="debug-message-index">
                            Message {idx + 1}
                          </span>
                        </div>
                        <div className="debug-message-content">
                          {Array.isArray(message.content) ? (
                            message.content.map((block, blockIdx) => (
                              <div
                                key={blockIdx}
                                className="debug-content-block"
                              >
                                {block.type === "text" && (
                                  <div className="debug-text-block">
                                    <span className="debug-block-label">
                                      💭 Reasoning:
                                    </span>
                                    <div className="debug-code">
                                      {block.text}
                                    </div>
                                  </div>
                                )}
                                {block.type === "tool_use" && (
                                  <div className="debug-tool-use-block">
                                    <span className="debug-block-label">
                                      🔧 Tool Call:
                                    </span>
                                    <div className="debug-tool-info">
                                      <strong>{block.name}</strong>
                                      <pre className="debug-code-json">
                                        {formatJson(block.input)}
                                      </pre>
                                    </div>
                                  </div>
                                )}
                                {block.type === "tool_result" && (
                                  <div className="debug-tool-result-block">
                                    <span className="debug-block-label">
                                      📥 Tool Result:
                                    </span>
                                    <pre className="debug-code-json">
                                      {typeof block.content === "string"
                                        ? block.content
                                        : formatJson(block.content)}
                                    </pre>
                                  </div>
                                )}
                              </div>
                            ))
                          ) : (
                            <div className="debug-code">{message.content}</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

          {/* Tools Used Section */}
          <div className="debug-section">
            <h3>🔧 Tools Used ({debugTrace.tools_used?.length || 0})</h3>
            <div className="debug-list">
              {debugTrace.tools_used?.map((tool, idx) => (
                <div key={idx} className="debug-list-item">
                  {tool}
                </div>
              ))}
            </div>
          </div>

          {/* Tool Results Section */}
          {debugTrace.tool_results &&
            Object.keys(debugTrace.tool_results).length > 0 && (
              <div className="debug-section">
                <h3>
                  📦 Tool Results ({Object.keys(debugTrace.tool_results).length}
                  )
                </h3>
                {Object.entries(debugTrace.tool_results).map(
                  ([toolKey, result]) => (
                    <div key={toolKey} className="debug-tool-result">
                      <button
                        className="debug-toggle-tool"
                        onClick={() => toggleToolResult(toolKey)}
                      >
                        {expandedSections.toolResults[toolKey] ? "▼" : "▶"}{" "}
                        {toolKey}
                      </button>
                      {expandedSections.toolResults[toolKey] && (
                        <pre className="debug-code-json">
                          {formatJson(result)}
                        </pre>
                      )}
                    </div>
                  ),
                )}
              </div>
            )}

          {/* Final Answer Section */}
          <div className="debug-section">
            <h3>
              ✅ Final Answer ({debugTrace.final_answer?.length || 0} chars)
            </h3>
            <div className="debug-code">{debugTrace.final_answer}</div>
          </div>

          {/* Tickers Mentioned */}
          {debugTrace.tickers_mentioned &&
            debugTrace.tickers_mentioned.length > 0 && (
              <div className="debug-section">
                <h3>📈 Tickers Mentioned</h3>
                <div className="debug-list">
                  {debugTrace.tickers_mentioned.map((ticker, idx) => (
                    <span key={idx} className="debug-ticker">
                      {ticker}
                    </span>
                  ))}
                </div>
              </div>
            )}

          {/* Error Information */}
          {debugTrace.error_occurred && (
            <div className="debug-section debug-error">
              <h3>❌ Error Information</h3>
              <div className="debug-code">
                <strong>Error Message:</strong> {debugTrace.error_message}
                {debugTrace.parse_error && (
                  <>
                    <br />
                    <strong>Parse Error:</strong> {debugTrace.parse_error}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Timestamp */}
          <div className="debug-section">
            <div className="debug-timestamp">
              🕐 {new Date(debugTrace.timestamp).toLocaleString()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatDebugModal;
